import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Snackbar } from '@lux/snackbar';
import { hideHeaderFlashMessage } from '@client/redux/modules/flashMessage/actions';
import { getHeaderFlashMessage } from '@client/redux/modules/flashMessage/selectors';
import * as S from './styles';

const FADE_DURATION_MS = 2000; // ms

const HIDE_TIMER_MS = 5000; // ms

interface FlashMessagesProps {
    hasSmartBanner?: boolean;
}

export const FlashMessages: FunctionComponent<FlashMessagesProps> = ({
    hasSmartBanner = false,
}) => {
    const timer = useRef<ReturnType<typeof setTimeout>>(null);
    const dispatch = useDispatch();

    const { type, message, action } = useSelector(getHeaderFlashMessage);

    const clearTimer = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    useEffect(() => {
        if (!type || !message) {
            return clearTimer;
        }

        clearTimer();

        if (!action) {
            timer.current = setTimeout(() => dispatch(hideHeaderFlashMessage()), HIDE_TIMER_MS);
        }

        return clearTimer;
    }, [type, message, action, dispatch, clearTimer]);

    return (
        <TransitionGroup>
            {type && message && (
                <CSSTransition
                    component="div"
                    timeout={{
                        enter: FADE_DURATION_MS,
                        exit: FADE_DURATION_MS,
                    }}
                >
                    <S.SnackbarWrapper hasSmartBanner={hasSmartBanner}>
                        <Snackbar
                            type={type}
                            content={message}
                            action={
                                action && {
                                    text: action.text,
                                    ctaCallback: action.callback,
                                }
                            }
                            visible
                        />
                    </S.SnackbarWrapper>
                </CSSTransition>
            )}
        </TransitionGroup>
    );
};

export default FlashMessages;
